// Rich text editor (what you see is what you get): bold, italic, underline, strikethrough, headings, lists, emoji.
// The text is stored as Markdown (shown with the same safe renderer everywhere and in the mails).
import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { editorText } from './lib/md';
import { EMOJI_GROUPS } from './lib/emoji';
import { cleanPastedLinks, webAddress } from './lib/editor';
import { Icon } from './ui';

export function MdEditor({
  id,
  label,
  value,
  onChange,
  rows = 8,
  placeholder,
  maxLength,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}) {
  const [emoji, setEmoji] = useState(false);
  // the link form under the toolbar: link text and web address
  const [link, setLink] = useState<{
    text: string;
    url: string;
    error: '' | 'address' | 'long';
    after: number | null;
  } | null>(null);
  const linkBtn = useRef<HTMLButtonElement>(null);
  /** puts the linked text in place of the selection (or the link at the cursor), or after a multi-paragraph selection */
  const insertLink = () => {
    if (!editor || !link) return;
    const url = webAddress(link.url);
    if (!url) return setLink({ ...link, error: 'address' });
    const chain = editor.chain().focus();
    if (link.after !== null) chain.setTextSelection(link.after);
    const done = chain
      // no text: the address itself is shown (without "mailto:" / "tel:")
      .insertContent({
        type: 'text',
        text: link.text.trim() || url.replace(/^(mailto|tel):/i, ''),
        marks: [{ type: 'link', attrs: { href: url } }],
      })
      .unsetMark('link') // typing after the link continues without it
      .run();
    // refused (e.g. the text limit is reached): keep what was typed and say why
    if (!done) return setLink({ ...link, error: 'long' });
    setLink(null);
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, protocols: ['http', 'https'], defaultProtocol: 'https' },
      }),
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      CharacterCount.configure({ limit: maxLength ?? null }),
    ],
    // no injected <style> tag: the app's Content Security Policy only allows style sheets from our own files
    injectCSS: false,
    content: editorText(value), // texts from the old Sportplaner may contain <b>…</b> etc.
    contentType: 'markdown',
    editorProps: {
      // pasted links: phone numbers cleaned, link types the page cannot show become plain text
      transformPastedHTML: cleanPastedLinks,
      attributes: {
        id,
        class: 'rich desc',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        'data-rows': String(Math.min(12, Math.max(3, rows))), // height via CSS (inline styles are blocked by the CSP)
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getMarkdown()),
  });
  // text replaced from outside (e.g. a template loaded later): show it
  useEffect(() => {
    if (editor && value !== editor.getMarkdown() && !editor.isFocused)
      editor.commands.setContent(editorText(value), { contentType: 'markdown', emitUpdate: false });
  }, [editor, value]);
  // which formats are active at the cursor (buttons look pressed)
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            h: e.isActive('heading'), // one button for headings (older texts may have levels 1 and 3)
            b: e.isActive('bold'),
            i: e.isActive('italic'),
            u: e.isActive('underline'),
            s: e.isActive('strike'),
            ul: e.isActive('bulletList'),
            ol: e.isActive('orderedList'),
            link: e.isActive('link'),
          }
        : null,
  });
  const run = (f: (c: NonNullable<typeof editor>['commands']) => void) => () => {
    if (!editor) return;
    editor.commands.focus();
    f(editor.commands);
  };
  const btn = (key: keyof NonNullable<typeof active>, ariaLabel: string, content: React.ReactNode, f: () => void) => (
    <button type="button" onClick={f} aria-label={ariaLabel} aria-pressed={!!active?.[key]}>
      {content}
    </button>
  );
  return (
    <div className="fl">
      <span className="fl-l" id={`${id}-label`}>
        {label}
      </span>
      <div className="md-bar" role="toolbar" aria-label="Formatierung">
        {/* one heading button: the toolbar fits in one row on a phone */}
        {btn(
          'h',
          'Überschrift',
          <b>H</b>,
          run(c =>
            editor?.isActive('heading') && !editor.isActive('heading', { level: 2 })
              ? c.setHeading({ level: 2 }) // an older level-1/3 heading becomes the usual one
              : c.toggleHeading({ level: 2 }),
          ),
        )}
        {btn(
          'b',
          'Fett',
          <b>B</b>,
          run(c => c.toggleBold()),
        )}
        {btn(
          'i',
          'Kursiv',
          <i>I</i>,
          run(c => c.toggleItalic()),
        )}
        {btn(
          'u',
          'Unterstrichen',
          <u>U</u>,
          run(c => c.toggleUnderline()),
        )}
        {btn(
          's',
          'Durchgestrichen',
          <s>S</s>,
          run(c => c.toggleStrike()),
        )}
        {btn(
          'ul',
          'Aufzählung',
          '• ≡',
          run(c => c.toggleBulletList()),
        )}
        {btn(
          'ol',
          'Nummerierte Liste',
          '1.≡',
          run(c => c.toggleOrderedList()),
        )}
        <button
          ref={linkBtn}
          type="button"
          onClick={() => {
            if (!editor || link) return setLink(null);
            setEmoji(false);
            // a link at the cursor: edit it; selected text in one paragraph: becomes the link text
            if (editor.isActive('link')) editor.chain().extendMarkRange('link').run();
            const { from, to, $from, $to } = editor.state.selection;
            const oneParagraph = $from.sameParent($to);
            const href = (editor.getAttributes('link') as { href?: string }).href ?? '';
            setLink({
              text: oneParagraph ? editor.state.doc.textBetween(from, to, ' ') : '',
              url: href,
              error: '',
              after: oneParagraph ? null : to, // across paragraphs: insert after the selection, never merge them
            });
          }}
          aria-label="Link einfügen"
          aria-pressed={!!active?.link}
          aria-expanded={!!link}
        >
          <Icon name="link" sw={2} />
        </button>
        <button
          type="button"
          onClick={() => {
            setLink(null);
            setEmoji(!emoji);
          }}
          aria-label="Emoji einfügen"
          aria-expanded={emoji}
        >
          🙂
        </button>
      </div>
      {link && (
        <div
          className="md-link"
          role="group"
          aria-label="Link"
          onKeyDown={ev => {
            // inside a <form>: Enter inserts the link – it must never submit (and notify) the whole form
            // (only in the two fields – Enter on "Link entfernen" must remove; not while a word is being composed)
            if (ev.key === 'Enter' && ev.target instanceof HTMLInputElement && !ev.nativeEvent.isComposing) {
              ev.preventDefault();
              insertLink();
            } else if (ev.key === 'Escape') {
              setLink(null);
              linkBtn.current?.focus();
            }
          }}
        >
          <input
            value={link.text}
            onChange={ev => setLink({ ...link, text: ev.target.value })}
            placeholder="Text, z. B. Anmeldung"
            aria-label="Link-Text"
          />
          <input
            type="url"
            inputMode="url"
            value={link.url}
            onChange={ev => setLink({ ...link, url: ev.target.value, error: '' })}
            placeholder="https://… oder name@…"
            aria-label="Adresse"
            aria-invalid={link.error === 'address'}
            aria-describedby={link.error ? `${id}-link-error` : undefined}
            autoFocus
          />
          <button type="button" className="btn btn-primary btn-sm" onClick={insertLink}>
            Einfügen
          </button>
          {active?.link && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                editor?.chain().focus().extendMarkRange('link').unsetLink().run();
                setLink(null);
              }}
            >
              Link entfernen
            </button>
          )}
          {link.error && (
            <p className="note warn" id={`${id}-link-error`} role="alert">
              {link.error === 'address'
                ? 'Bitte eine Web- oder E-Mail-Adresse eingeben, z. B. tri-team.de'
                : 'Der Text ist zu lang – bitte kürzen, dann den Link einfügen.'}
            </p>
          )}
        </div>
      )}
      {emoji && (
        <div className="emoji-pop">
          {EMOJI_GROUPS.map(g => (
            <div key={g.label} className="emoji-group" role="group" aria-label={g.label}>
              <small aria-hidden="true">{g.label}</small>
              {g.list.map(x => (
                <button
                  key={x}
                  type="button"
                  onClick={() => {
                    editor?.chain().focus().insertContent(x).run();
                    setEmoji(false);
                  }}
                >
                  {x}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
